/**
 * Screen 03 — two-factor.
 *
 * Wired to GoTrue's REAL MFA API (`listFactors` / `challenge` / `verify`), not a code box
 * that accepts any six digits. That distinction is the whole point: a security screen
 * which checks nothing is worse than no screen, because it teaches the operator that 2FA
 * is protecting them when it is not.
 *
 * Consequently this screen only appears when the account has a VERIFIED TOTP factor. With
 * none enrolled, GoTrue issues a fully-elevated session at password sign-in and the flow
 * goes straight through — which is correct, and visible, rather than a fake step.
 *
 * The chromatic-aberration digits are from the design: each digit is drawn three times,
 * offset in amber and cyan under a white top layer, in screen blend. Decorative, and kept
 * because it makes the code slots feel like instrument panel readouts rather than a form.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { humanError } from '@/lib/human-error';

const CODE_LENGTH = 6;

export function MfaScreen({
  email,
  factorId,
  onRestart,
}: {
  email: string;
  factorId: string;
  onRestart: () => void;
}): ReactNode {
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);

  /*
   * A challenge is requested once, up front. GoTrue ties the verification to a challenge
   * id, so requesting one per keystroke would invalidate the previous and make a
   * correctly-typed code fail.
   */
  useEffect(() => {
    let alive = true;
    void supabase.auth.mfa.challenge({ factorId }).then(({ data, error: err }) => {
      if (!alive) return;
      if (err) setError(err.message);
      else setChallengeId(data?.id ?? null);
    });
    return () => {
      alive = false;
    };
  }, [factorId]);

  useEffect(() => {
    input.current?.focus();
  }, []);

  const slots = useMemo(
    () =>
      Array.from({ length: CODE_LENGTH }, (_, i) => ({
        char: code[i] ?? '',
        filled: i < code.length,
        caret: i === code.length,
      })),
    [code],
  );

  async function verify(value: string): Promise<void> {
    if (!challengeId || value.length !== CODE_LENGTH) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.auth.mfa.verify({
      factorId,
      challengeId,
      code: value,
    });
    if (err) {
      setError(err.message.toLowerCase().includes('invalid') ? 'That code was not accepted. Enter the current code from your authenticator app.' : humanError(err, 'That code could not be verified. Try again.'));
      setCode('');
      /* A fresh challenge: GoTrue consumes the previous one even on failure, so reusing
         it would reject the next correct code too. */
      const { data } = await supabase.auth.mfa.challenge({ factorId });
      setChallengeId(data?.id ?? null);
      input.current?.focus();
    }
    // On success the session is elevated and the auth gate swaps the tree; no state to set.
    setBusy(false);
  }

  return (
    <section style={{ width: '100%', maxWidth: 420, animation: RISE }}>
      <h1 style={h1}>One more step, for the record.</h1>
      <p style={sub}>
        Enter the six-digit code from your authenticator app. Reports carry the name of
        whoever signed them, so we make sure it&rsquo;s really you.
      </p>

      <div style={{ position: 'relative', marginTop: 32, width: 'fit-content' }}>
        {/* One real input behind the slots — the slots are presentation. This is what
            keeps paste, autofill and mobile keyboards working, which six separate inputs
            reliably break. */}
        <input
          ref={input}
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label="Six-digit authentication code"
          value={code}
          onChange={(e) => {
            const next = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
            setCode(next);
            if (next.length === CODE_LENGTH) void verify(next);
          }}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            border: 0,
            padding: 0,
            cursor: 'text',
            font: 'inherit',
          }}
        />
        <div onClick={() => input.current?.focus()} style={{ display: 'flex', gap: 9, cursor: 'text' }}>
          {slots.map((slot, i) => (
            <div
              key={i}
              style={{
                width: 48,
                height: 62,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 10,
                background: slot.filled ? '#141a26' : '#12161f',
                border: `1px solid ${slot.caret ? '#36c5d6' : slot.filled ? '#2f3646' : '#283041'}`,
                boxShadow: slot.caret ? '0 0 0 3px rgba(54,197,214,0.16)' : 'none',
              }}
            >
              {slot.filled && (
                <div
                  style={{
                    position: 'relative',
                    fontWeight: 600,
                    fontSize: 30,
                    lineHeight: 1,
                    letterSpacing: '-0.03em',
                    fontFeatureSettings: "'tnum' 1",
                  }}
                >
                  {/* Chromatic aberration: amber and cyan offsets under a white top. */}
                  <span style={aberration('#f2a23c', -2, -1)}>{slot.char}</span>
                  <span style={aberration('#36c5d6', 2, 1)}>{slot.char}</span>
                  <span style={{ position: 'relative', color: '#fafafa' }}>{slot.char}</span>
                </div>
              )}
              {slot.caret && (
                <span
                  style={{
                    width: 2,
                    height: 28,
                    background: '#36c5d6',
                    boxShadow: '0 0 8px #36c5d6',
                    animation: 'lm-caret 1100ms steps(1, end) infinite',
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {error && (
        <p role="alert" style={errorNote}>
          <span style={errorDot} />
          {error}
        </p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 22, marginTop: 30 }}>
        <button
          type="button"
          onClick={() => void verify(code)}
          disabled={busy || code.length !== CODE_LENGTH || !challengeId}
          className="lm-si-amber"
          style={{
            minHeight: 48,
            paddingInline: 26,
            border: 0,
            borderRadius: 10,
            font: 'inherit',
            fontSize: 15,
            fontWeight: 600,
            color: '#18181b',
            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
            boxShadow: busy || code.length !== CODE_LENGTH ? 'none' : '0 6px 26px rgba(245,158,11,0.3)',
            cursor: busy || code.length !== CODE_LENGTH ? 'not-allowed' : 'pointer',
            opacity: busy || code.length !== CODE_LENGTH ? 0.45 : 1,
            whiteSpace: 'nowrap',
            flex: 'none',
          }}
        >
          {busy ? 'Verifying…' : 'Verify and continue'}
        </button>
      </div>

      <p style={{ fontSize: 13, lineHeight: 1.6, margin: '28px 0 0', color: '#71717a' }}>
        Signing in as <span style={{ color: '#d4d4d8' }}>{email}</span>. Not you?{' '}
        <a
          href="#signin"
          className="lm-si-link"
          onClick={(e) => {
            e.preventDefault();
            onRestart();
          }}
        >
          Start over
        </a>
        .
      </p>
    </section>
  );
}

const RISE = 'lm-rise 340ms cubic-bezier(0.2,0.7,0.2,1) both';

const h1 = {
  fontWeight: 600,
  fontSize: 'clamp(28px, 2.8vw, 38px)',
  lineHeight: 1.1,
  letterSpacing: '-0.04em',
  margin: 0,
  color: '#fafafa',
} as const;

const sub = { fontSize: 15, lineHeight: 1.6, margin: '14px 0 0', color: '#a1a1aa' } as const;

const errorNote = {
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  margin: '18px 0 0',
  padding: '11px 13px',
  fontSize: 13.5,
  lineHeight: 1.5,
  color: '#fca5a5',
  background: 'rgba(248,113,113,0.09)',
  border: '1px solid rgba(248,113,113,0.28)',
  borderRadius: 10,
} as const;

const errorDot = {
  flex: 'none',
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: '#f87171',
  boxShadow: '0 0 8px #f87171',
} as const;

function aberration(colour: string, dx: number, dy: number) {
  return {
    position: 'absolute' as const,
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colour,
    mixBlendMode: 'screen' as const,
    transform: `translate(${dx}px, ${dy}px)`,
  };
}
